import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react';
import { useNavigateParams } from '../../../../hooks/useNavigateParams';
import { Context } from '../../../../context/UserContext';

import moment from 'moment';
import 'moment/locale/pt-br';

import styles from '../../../form/Form.module.css';
import Form from '../Form';

import useFlashMessage from '../../../../hooks/useFlashMessage';

import CRUDsAPI from '../../../../api/CRUDs';
import * as MovimentoAPI from '../../../../api/movimento';
import * as AmbienteAPI from '../../../../api/ambiente';
import * as EmpresaAPI from '../../../../api/empresa';
import * as OperacaoAPI from '../../../../api/operacao';
import * as PlantaoAPI from '../../../../api/plantao';
import * as SetorAPI from '../../../../api/setor';
import * as TabelaAPI from '../../../../api/tabela';

import { obterIndexadores } from '../../../../api/custos';

//helpers
import { resolveEmpresaTabela } from '../../../../utils/funcoes';
import { getEmpresaScope } from '../../../../helpers/sessionEmpresa';
import { normalizeMovimentoPayload } from '../../../../utils/movimento/normalizeMovimentoPayload';

import { MOVIMENTO_DEFAULTS } from '../shared/constants';

const CreateOrEdit = () => {
    const { parametro } = useContext(Context);

    // LocalStorage (empresa / multi-empresa)
    const { pessoaId, empresaId, empresaIds } = getEmpresaScope();
    const empresaTabelaId = resolveEmpresaTabela(parametro, empresaId);

    // -----------------------------
    // Base dados (movimento)
    // -----------------------------
    const [dados, setDados] = useState(() => ({
        empresaId,
        empresaIds,
        ativo: true,
        createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
        updatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
        data: moment().format('YYYY-MM-DD 00:00:00'),
        dataPrevista: moment().format('YYYY-MM-DD 00:00:00'),
        primeiraDataVencimento: moment().startOf('day').format('YYYY-MM-DD 00:00'),
    }));

    const token = useState(localStorage.getItem('token')) || '';
    const [shouldCloseAfterCreate, setShouldCloseAfterCreate] = useState(false);

    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    // -----------------------------
    // URL params
    // -----------------------------
    const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const pathname = '/Movimento';

    const ul = urlParams.get('ul');
    const li = urlParams.get('li');
    const tipo = urlParams.get('tipo');
    const operacaoIdParam = urlParams.get('operacaoId'); // pode vir null
    const operacaoUUID = urlParams.get('operacao');
    const uuid = urlParams.get('uuid');
    const btnText = urlParams.get('btnText') || MOVIMENTO_DEFAULTS.BTN_TEXT_CADASTRAR;
    const natureza = urlParams.get('natureza'); // "Entrada" | "Saida" | ...
    const origem = urlParams.get('origem') || '';

    const params = useMemo(() => {
        return new URLSearchParams({
            ul: ul ?? '',
            li: li ?? '',
            tipo: tipo ?? '',
            operacao: operacaoUUID ?? '',
            uuid: uuid ?? '',
            btnText: btnText ?? '',
            natureza: natureza ?? '',
            origem: origem ?? '',
        });
    }, [ul, li, tipo, operacaoUUID, uuid, btnText, natureza, origem]);

    // -----------------------------
    // Operação + parâmetros + plantão
    // -----------------------------
    const [operacao, setOperacao] = useState({});
    const [operacaoParametros, setOperacaoParametros] = useState({});
    const [operacaoParametrosDocumentos, setOperacaoParametrosDocumentos] = useState([]);
    const { plantaoTipoId } = operacaoParametros ?? {};
    const [plantao, setPlantao] = useState({});

    // -----------------------------
    // Options / Enums
    // -----------------------------
    const [OptionsSituacao, setOptionsSituacao] = useState([]);
    const [OptionsSituacaoItem, setOptionsSituacaoItem] = useState([]);

    const [OptionsCentroCusto, setOptionsCentroCusto] = useState([]);
    const [OptionsEmpresa, setEmpresa] = useState([]);
    const [OptionsSetor, setOptionsSetor] = useState([]);
    const [OptionsAmbiente, setOptionsAmbiente] = useState([]);
    const [OptionsTabelaPreco, setTabelaPreco] = useState([]);

    //Enums locação de imoveis
    const [enumCondominio, setEnumCondominio] = useState([]);
    const [enumDiaVencimento, setEnumDiaVencimento] = useState([]);
    const [enumLocacao, setEnumLocacao] = useState([]);
    const [enumIPTU, setEnumIPTU] = useState([]);
    const [enumContrato, setEnumContrato] = useState([]);
    const [enumEnergia, setEnumEnergia] = useState([]);
    const [enumAgua, setEnumAgua] = useState([]);

    const [enumTipoAditivo, setEnumTipoAditivo] = useState([]);

    // frete enums
    const [enumTipoFrete, setEnumTipoFrete] = useState([]);
    const [enumResponsavelFinanceiro, setEnumResponsavelFinanceiro] = useState([]);

    const [indexadores, setIndexadores] = useState([]);

    // ✅ guard para não recarregar tabela de preço do editar em loop
    const tabelaCarregadaRef = useRef(false);
    // quando a operação esta configurada para usar tabela de preços na lista de produtos obrigatoriamente em VENDAS
    const modoTabelaPreco = operacaoParametros?.formTabelaOrProduto === true;
    const centroCustoLimiteBloquearNaDigitacao = operacaoParametros?.centroCustoLimiteBloquearNaDigitacao === true;
    const centroCustoLimiteBloquear = operacaoParametros?.centroCustoLimiteBloquear === true;
    // -----------------------------
    // Enums (mantive seu padrão)
    // -----------------------------
    const consultaEnum = useCallback(async (table, field, filter) => {
        const data = await TabelaAPI.enums(table, field, filter);

        const movimentosFields = {
            condominio: setEnumCondominio,
            diaVencimento: setEnumDiaVencimento,
            locacao: setEnumLocacao,
            iptu: setEnumIPTU,
            contrato: setEnumContrato,
            energia: setEnumEnergia,
            agua: setEnumAgua,
        };

        const aditivosFields = {
            tipo: setEnumTipoAditivo,
        };

        const fretesFields = {
            tipoFrete: setEnumTipoFrete,
            responsavelFinanceiro: setEnumResponsavelFinanceiro,
        };

        if (table === 'Movimentos' && movimentosFields[field]) {
            movimentosFields[field](data);
        } else if (table === 'movimentoAditivos' && aditivosFields[field]) {
            aditivosFields[field](data);
        } else if (table === 'movimentoFretes' && fretesFields[field]) {
            fretesFields[field](data);
        }
    }, []);

    // -----------------------------
    // Consultas auxiliares
    // -----------------------------
    const consultaEmpresa = useCallback(async () => {
        const result = await EmpresaAPI.empresa(empresaId);
        setEmpresa(result);
        return result;
    }, [empresaId]);

    const consultaSetor = useCallback(async (obj) => {
        try {
            const result = await SetorAPI.obterSetores(obj);
            setOptionsSetor(result);
            return result;
        } catch (error) {
            console.error('Erro ao consultar setores:', error);
            return [];
        }
    }, []);

    const consultaAmbiente = useCallback(async (obj) => {
        try {
            const result = await AmbienteAPI.obterAmbientes(obj);
            setOptionsAmbiente(result);
            return result;
        } catch (error) {
            console.error('Erro ao consultar ambientes:', error);
            return [];
        }
    }, []);

    const obterIndexador = useCallback(async (obj) => {
        try {
            const data = await obterIndexadores(obj);
            setIndexadores(data);
            return data;
        } catch (error) {
            console.error('Erro ao obter indexadores:', error);
            return [];
        }
    }, []);

    const consultaSituacao = useCallback(async (obj) => {
        const { uuid, empresaId } = obj;
        if (!uuid || !empresaId) return [];

        try {
            const result = await OperacaoAPI.situacaoByOperacao(obj);

            if (obj?.operacao?.sigla === 'INV') {
                const filtrados = (result || []).filter(
                    (item) => item.descricao?.toLowerCase() === 'aberto' || item.descricao?.toLowerCase() === 'fechado'
                );
                setOptionsSituacao(filtrados);
                return filtrados;
            }

            if (obj?.operacao?.sigla === 'CFR') {
                const filtrados = (result || [])
                    .filter((x) => x.descricao !== 'Transformado')
                    .map((x) => ({
                        id: x.id,
                        descricao: x.descricao,
                        titulo: x.titulo,
                        label: x.descricao,
                        value: x.descricao,
                    }));
                setOptionsSituacao(result || []);
                setOptionsSituacaoItem(filtrados);
                return result ?? [];
            }

            setOptionsSituacao(result || []);
            return result || [];
        } catch (error) {
            console.error('Erro ao consultar situação:', error);
            return [];
        }
    }, []);

    const consultaCentroCusto = useCallback(async (obj) => {
        const { uuid, empresaId } = obj;
        if (!uuid || !empresaId) return [];

        try {
            const result = await OperacaoAPI.centroCustoByOperacao(obj);
            setOptionsCentroCusto(result || []);
            return result || [];
        } catch (e) {
            console.error('Erro ao consultar centro de custo:', e);
            return [];
        }
    }, []);

    // -----------------------------
    // Plantão
    // -----------------------------
    const consultaPlantao = useCallback(
        async ({ empresaId, plantaoTipoId, data }) => {
            const dataPlantao = await PlantaoAPI.obterPlantaoNaoConcluido({ empresaId, plantaoTipoId, data });

            if (!dataPlantao && operacaoParametros?.plantao) {
                setFlashMessage('Não existe plantão aberto!\nParâmetros dessa rotina obriga um plantão', 'error');
                setTimeout(() => navigate(pathname, params), 3000);
                return null;
            }

            setDados((prev) => ({
                ...prev,
                plantaoId: dataPlantao?.id ?? null,
            }));

            setPlantao({
                plantaoId: dataPlantao?.id ?? null,
                plantaoTipoId,
                plantao: true,
            });

            return dataPlantao;
        },
        [operacaoParametros?.plantao, navigate, pathname, params, setFlashMessage]
    );

    // -----------------------------
    // Tabela de preço (parceiro)
    // -----------------------------
    const carregarTabelaPrecoParceiro = useCallback(
        async ({ empresaId, pessoaId, operacaoId, entrada, liberada, locacao, saida, servico }) => {
            if (!pessoaId) {
                setTabelaPreco([]);
                return [];
            }

            const result = await TabelaAPI.precoByPessoaId({
                empresaId,
                pessoaId,
                operacaoId,
                entrada,
                liberada,
                locacao,
                saida,
                servico,
            });

            setTabelaPreco(result || []);
            return result || [];
        },
        []
    );

    // ---------------------------------------------------------------------------------------------------------------
    // FUNÇÕES UTILIZADAS Carrega OPERAÇÃO + PARÂMETROS + OPTIONS
    // ---------------------------------------------------------------------------------------------------------------
    const retornaOperacao = useCallback(
        async (uuidOperacao) => {
            if (!uuidOperacao) return null;

            try {
                const resultado = await OperacaoAPI.operacao(uuidOperacao);
                setOperacao(resultado || {});

                const paramsOp = await OperacaoAPI.retornaOperacaoParametros(resultado.id, dados.empresaId);
                setOperacaoParametros(paramsOp || {});
                setDados((prev) => {
                    if (prev.contaId === null) {
                        return { ...prev, contaId: paramsOp?.lancamentoContaId };
                    }
                    return prev; // Retorna o estado sem alterações se não for null
                });

                setOperacaoParametrosDocumentos(paramsOp?.operacaoParametrosDocumentos || []);

                const obj = {
                    uuid: operacaoUUID,
                    empresaId: dados.empresaId,
                    operacao: resultado,
                };

                await consultaSituacao(obj);
                await consultaSituacao({ ...obj, movimentoItem: true });
                await consultaCentroCusto(obj);

                return resultado;
            } catch (erro) {
                console.error(erro);
                return null;
            }
        },
        [dados.empresaId, operacaoUUID, consultaSituacao, consultaCentroCusto]
    );
    //extrai tabelaPrecos da operacaoParametros
    const obterTabelaPrecoByOperacaoParametros = async (operacaoParametros) => {
        // garante array seguro
        const listaTP = operacaoParametros?.operacaoParametroTPs ?? [];

        // extrai todos os objetos tabela de preco válidos
        const data = listaTP.map((tp) => tp?.tabelaPreco).filter((tp) => tp && Object.keys(tp).length > 0); // remove nulos/vazios

        if (!data) return;
        // seta no estado
        setTabelaPreco(data);

        return data;
    };

    // ---------------------------------------------------------
    // Effect A: Inicial (operações/empresa/setor/ambiente/indexadores/enums)
    // ---------------------------------------------------------
    useEffect(() => {
        if (!operacaoUUID) return;

        retornaOperacao(operacaoUUID);
        consultaEmpresa();
        consultaSetor({});
        consultaAmbiente({});
        obterIndexador({ empresaId: empresaIds, data: moment().format('YYYY-MM-DD') });

        //enums locação de imóveis
        consultaEnum('Movimentos', 'condominio');
        consultaEnum('Movimentos', 'diaVencimento');
        consultaEnum('Movimentos', 'locacao');
        consultaEnum('Movimentos', 'iptu');
        consultaEnum('Movimentos', 'contrato');
        consultaEnum('Movimentos', 'energia');
        consultaEnum('Movimentos', 'agua');

        //enums aditivos
        consultaEnum('movimentoAditivos', 'tipo');

        // enums frete (se você precisar aqui)
        consultaEnum('movimentoFretes', 'tipoFrete');
        consultaEnum('movimentoFretes', 'responsavelFinanceiro');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operacaoUUID]);

    // ---------------------------------------------------------
    // Effect B: Carregar MOVIMENTO (editar)
    // ---------------------------------------------------------
    const lastFetchKeyRef = useRef(null);
    useEffect(() => {
        if (!token) return navigate('/Auth', { replace: true });
        if (!uuid || uuid === 'null') return;

        const key = `${uuid}|${operacaoIdParam || ''}`;

        // ✅ guard anti-loop: se já buscou esse uuid/operação, não busca de novo
        if (lastFetchKeyRef.current === key) return;
        lastFetchKeyRef.current = key;

        CRUDsAPI.movimento
            .edit(uuid, operacaoIdParam)
            .then((response) => {
                const mov = response.data;
                setDados(mov);
            })
            .catch((error) => {
                console.error(error);
                setFlashMessage(error?.response?.data?.message, error?.response?.data?.type);
            });
    }, [token, navigate, uuid, operacaoIdParam, setFlashMessage]);

    // ---------------------------------------------------------
    // Effect C: aplicar defaults de operacaoParametros + plantão
    // ---------------------------------------------------------
    useEffect(() => {
        if (!operacaoParametros?.operacaoId) return;

        // defaults de operacaoId/situacaoId no movimento (sem loop)
        if (operacaoParametros?.situacaoId) {
            setDados((prev) => {
                const nextOperacaoId = operacaoParametros.operacaoId;
                const nextSituacaoId = operacaoParametros.situacaoId;

                if (prev.operacaoId === nextOperacaoId && prev.situacaoId === nextSituacaoId) return prev;

                return {
                    ...prev,
                    operacaoId: nextOperacaoId,
                    situacaoId: nextSituacaoId,
                };
            });
        }

        // plantão: só quando tem base
        if (dados?.empresaId && plantaoTipoId && dados?.data) {
            consultaPlantao({ empresaId: dados.empresaId, plantaoTipoId, data: dados.data });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operacaoParametros?.operacaoId, operacaoParametros?.situacaoId, plantaoTipoId, dados?.empresaId, dados?.data]);

    // ---------------------------------------------------------
    // Effect D: Carregar TABELA PREÇO no EDITAR (se usa tabela)
    // ---------------------------------------------------------
    useEffect(() => {
        const isEditar = btnText?.toLowerCase() === MOVIMENTO_DEFAULTS.BTN_TEXT_ATUALIZAR.toLowerCase();

        if (!isEditar) return;
        //if (!modoTabelaPreco) return;

        if (!dados?.parceiroId || !dados?.empresaId) return;
        if (!operacao?.id) return;

        if (tabelaCarregadaRef.current) return;
        tabelaCarregadaRef.current = true;

        carregarTabelaPrecoParceiro({
            empresaId: dados.empresaId,
            pessoaId: dados.parceiroId,
            operacaoId: operacao?.id, // se quiser filtrar por operação também
            entrada: natureza === 'Entrada' ? !!operacao?.entrada : false,
            saida: natureza === 'Saida' ? !!operacao?.saida : false,
            liberada: true,
        }).catch((e) => {
            console.error('Erro ao carregar tabela preço (editar):', e);
            tabelaCarregadaRef.current = false;
        });
    }, [
        btnText,
        modoTabelaPreco,
        dados?.parceiroId,
        dados?.empresaId,
        operacao?.id,
        operacao?.entrada,
        operacao?.saida,
        natureza,
        carregarTabelaPrecoParceiro,
    ]);

    // ---------------------------------------------------------
    // managerDados (mantive seu fluxo, só organizei um pouco)
    // ---------------------------------------------------------
    async function managerDados(obj) {
        let urlNavigate = pathname;

        // ✅ apenas itens pedido === true
        const payloadOriginal = {
            ...obj,
            movimentoItems: (obj.movimentoItems || []).filter((item) => item.pedido === true),
        };

        let payload;
        try {
            payload = normalizeMovimentoPayload({ movimento: obj, operacao, operacaoParametros });
        } catch (error) {
            setFlashMessage(error.message || 'Confira as parcelas na aba Lançamentos.', 'warning');
            return;
        }
        const tamanhoOriginal = new Blob([JSON.stringify(payloadOriginal)]).size;
        const tamanhoSlim = new Blob([JSON.stringify(payload)]).size;

        console.info('=== Comparação Payload ===');
        console.info({
            itens: payload.movimentoItems.length,
            originalKB: (tamanhoOriginal / 1024).toFixed(2),
            slimKB: (tamanhoSlim / 1024).toFixed(2),
            reducaoKB: ((tamanhoOriginal - tamanhoSlim) / 1024).toFixed(2),
            reducaoPercentual: (((tamanhoOriginal - tamanhoSlim) / tamanhoOriginal) * 100).toFixed(2) + '%',
        });

        try {
            let response;
            let tempo = 1000;
            if (operacao?.balanco === true && !obj?.id) {
                response = await MovimentoAPI.iniciarBalanco(payload);
            } else if (uuid && uuid !== 'null') {
                response = await CRUDsAPI.movimento.editar(payload);
            } else {
                response = await CRUDsAPI.movimento.cadastrar(payload);

                // ✅ cadastro OK (200): não desmonta o form
                if (response.status === 200) {
                    setShouldCloseAfterCreate(true);

                    const newRow = response.data?.data;
                    if (newRow) {
                        setDados(newRow);
                        params.set('uuid', newRow.uuid);
                        params.set('btnText', 'Atualizar');
                    }

                    setFlashMessage(response.data.message, response.data.type);
                    return;
                }

                // mantém fluxo antigo
                if (response.status === 202) {
                    setDados(response.data);
                    response.data.message = 'Registro em duplicidade!';
                    response.data.type = 'warning';
                    urlNavigate = pathname + '/CreateOrEdit';
                    params.set('uuid', response.data.uuid);
                    params.set('btnText', 'Atualizar');
                } else if (response.status === 422) {
                    tempo = 6000;
                    params.set('btnText', 'Cadastrar');
                }
            }

            setFlashMessage(response.data.message, response.data.type);

            setTimeout(() => {
                navigate(urlNavigate, params);
            }, parametro.msgTempo * tempo);
        } catch (error) {
            console.error('ERRO managerDados', error);

            if (error?.response?.status === 413) {
                console.error('Payload excedeu o limite do servidor.', {
                    tamanhoKB: Math.round(new Blob([JSON.stringify(payload)]).size / 1024),
                    itens: payload.movimentoItems?.length,
                });
                setFlashMessage(
                    'Não foi possível gravar o movimento porque a quantidade de informações enviadas excedeu o limite permitido pelo servidor. Entre em contato com o administrador do sistema.',
                    'warning',
                    10
                );
                return;
            }

            setFlashMessage(error?.response?.data?.message || error?.message, error?.response?.data?.type || 'error');
        }
    }

    return (
        <section className={`${styles.form_container} ${styles[parametro.tema]}`}>
            <Form
                token={token}
                handleSubmit={managerDados}
                dados={dados}
                setDados={setDados}
                btnText={btnText}
                // parâmetros
                parametro={parametro}
                operacao={operacao}
                operacaoParametros={operacaoParametros}
                operacaoParametrosDocumentos={operacaoParametrosDocumentos}
                natureza={natureza}
                plantao={plantao}
                indexadores={indexadores}
                // funções
                onCarregarTabelaPreco={setTabelaPreco}
                obterTabelaPrecoByOperacaoParametros={obterTabelaPrecoByOperacaoParametros}
                // options
                OptionsTabelaPreco={OptionsTabelaPreco}
                OptionsSituacao={OptionsSituacao}
                OptionsSituacaoItem={OptionsSituacaoItem}
                OptionsCentroCusto={OptionsCentroCusto}
                OptionsEmpresa={OptionsEmpresa}
                OptionsSetor={OptionsSetor}
                OptionsAmbiente={OptionsAmbiente}
                // enums fretes
                enumFreteTipo={enumTipoFrete}
                enumFreteResponsavelFinanceiro={enumResponsavelFinanceiro}
                //enums locação de imóveis
                enumTipoAditivo={enumTipoAditivo}
                enumCondominio={enumCondominio}
                enumDiaVencimento={enumDiaVencimento}
                enumLocacao={enumLocacao}
                enumIPTU={enumIPTU}
                enumContrato={enumContrato}
                enumEnergia={enumEnergia}
                enumAgua={enumAgua}
                //industria
                dataPlantao={plantao}
                // empresas
                pessoaId={pessoaId}
                empresaId={empresaId}
                empresaIds={empresaIds}
                // fechamento
                shouldCloseAfterCreate={shouldCloseAfterCreate}
                setShouldCloseAfterCreate={setShouldCloseAfterCreate}
                // voltar
                caminhoVoltar={pathname}
                // variável
                modoTabelaPreco={modoTabelaPreco}
                centroCustoLimiteBloquearNaDigitacao={centroCustoLimiteBloquearNaDigitacao}
                centroCustoLimiteBloquear={centroCustoLimiteBloquear}
                setFlashMessage={setFlashMessage}
            />
        </section>
    );
};

export default CreateOrEdit;
